import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ShieldCheck,
  AlertTriangle,
  Clock,
  Hourglass,
  Search,
  Calendar,
  Filter,
  Download,
  Copy,
  Eye,
  MoreHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Award,
  Cpu,
  Plus,
} from 'lucide-react';

const tabs = [
  'Overview',
  'Timestamp Records',
  'Verification',
  'Time Sources',
  'Authorities',
  'Settings',
];

export default function TimestampRecords() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Timestamp Records');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterAuthority, setFilterAuthority] = useState('All');
  const [fromDate, setFromDate] = useState('01/05/2025');
  const [toDate, setToDate] = useState('31/05/2025');

  const metrics = [
    {
      title: 'Total Timestamps',
      value: '12,458',
      trend: '▲ 18.6% vs last month',
      icon: <FileText className="h-4 w-4 text-purple-600" />,
      iconBg: 'bg-purple-50',
      trendColor: 'text-emerald-600',
    },
    {
      title: 'Verified Timestamps',
      value: '12,201',
      trend: '▲ 17.2% vs last month',
      icon: <ShieldCheck className="h-4 w-4 text-emerald-600" />,
      iconBg: 'bg-emerald-50',
      trendColor: 'text-emerald-600',
    },
    {
      title: 'Failed Verifications',
      value: '18',
      trend: '▼ 10.0% vs last month',
      icon: <AlertTriangle className="h-4 w-4 text-rose-600" />,
      iconBg: 'bg-rose-50',
      trendColor: 'text-rose-600',
    },
    {
      title: 'Pending Verifications',
      value: '239',
      trend: '▲ 5.6% vs last month',
      icon: <Clock className="h-4 w-4 text-amber-600" />,
      iconBg: 'bg-amber-50',
      trendColor: 'text-emerald-600',
    },
    {
      title: 'Expired Timestamps',
      value: '3',
      trend: '▼ 40.0% vs last month',
      icon: <Hourglass className="h-4 w-4 text-blue-600" />,
      iconBg: 'bg-blue-50',
      trendColor: 'text-rose-600',
    },
  ];

  const records = [
    {
      id: 'TS-2025-05-31-001',
      type: 'Document',
      file: 'Service_Agreement.pdf',
      hash: 'a3f2c6e5d4b7e9f1123c8a6d5be...',
      timestamp: 'May 31, 2025 10:30:15 AM (UTC)',
      authority: 'I-PQSign TSA 01',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:35:22 AM',
    },
    {
      id: 'TS-2025-05-31-002',
      type: 'Artifact',
      file: 'Firmware_v2.1.bin',
      hash: 'b7e9d2a1f8c3d6a7b9f1e2c8d4a6...',
      timestamp: 'May 31, 2025 10:28:44 AM (UTC)',
      authority: 'I-PQSign TSA 02',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:33:11 AM',
    },
    {
      id: 'TS-2025-05-31-003',
      type: 'Certificate',
      file: 'John_Smith_Cert.pem',
      hash: 'c1a7b8d9e2f3a4b5c6d7e8f9a0b1...',
      timestamp: 'May 31, 2025 10:25:31 AM (UTC)',
      authority: 'GlobalSign TSA',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:30:02 AM',
    },
    {
      id: 'TS-2025-05-31-004',
      type: 'Document',
      file: 'NDA_Contract.pdf',
      hash: 'd5fe6a7b8c9d0e1f2a3b4c5d6e7...',
      timestamp: 'May 31, 2025 10:23:05 AM (UTC)',
      authority: 'I-PQSign TSA 01',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:27:51 AM',
    },
    {
      id: 'TS-2025-05-31-005',
      type: 'Artifact',
      file: 'Mobile_App_release.apk',
      hash: 'e3d4c5b6a7b8c9d0e1f2a3b4c5d6...',
      timestamp: 'May 31, 2025 10:19:12 AM (UTC)',
      authority: 'Sectigo TSA',
      status: 'Pending',
      verificationTime: '—',
    },
    {
      id: 'TS-2025-05-31-006',
      type: 'Document',
      file: 'Board_Resolution.pdf',
      hash: 'f4e5d6c7b8a9d0e1f2b3c4d5e6f7...',
      timestamp: 'May 31, 2025 10:15:47 AM (UTC)',
      authority: 'GlobalSign TSA',
      status: 'Failed',
      verificationTime: 'May 31, 2025 10:16:20 AM',
    },
    {
      id: 'TS-2025-05-31-007',
      type: 'Artifact',
      file: 'Driver_Update_v1.0.exe',
      hash: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4...',
      timestamp: 'May 31, 2025 10:10:33 AM (UTC)',
      authority: 'I-PQSign TSA 02',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:15:05 AM',
    },
    {
      id: 'TS-2025-05-31-008',
      type: 'Document',
      file: 'Policy_Document.pdf',
      hash: 'b2c3d4e5f6g7h8i9j0k1l2m3n4o5...',
      timestamp: 'May 31, 2025 10:05:22 AM (UTC)',
      authority: 'Sectigo TSA',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:10:01 AM',
    },
    {
      id: 'TS-2025-05-31-009',
      type: 'Certificate',
      file: 'Server_Cert.crt',
      hash: 'c3d4e5f6g7h8i9j0k1l2m3n4o5p6...',
      timestamp: 'May 31, 2025 09:58:42 AM (UTC)',
      authority: 'GlobalSign TSA',
      status: 'Verified',
      verificationTime: 'May 31, 2025 10:03:18 AM',
    },
    {
      id: 'TS-2025-05-31-010',
      type: 'Document',
      file: 'Invoice_May2025.pdf',
      hash: 'd4e5f6g7h8i9j0k1l2m3n4o5p6q7...',
      timestamp: 'May 31, 2025 09:50:11 AM (UTC)',
      authority: 'I-PQSign TSA 01',
      status: 'Verified',
      verificationTime: 'May 31, 2025 09:55:10 AM',
    },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Artifact':
        return <Cpu className="h-3.5 w-3.5 text-slate-500 shrink-0" />;
      case 'Certificate':
        return <Award className="h-3.5 w-3.5 text-slate-500 shrink-0" />;
      default:
        return <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Verified':
        return (
          <span className="inline-flex items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">Verified</span>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">Pending</span>
        );
      case 'Failed':
        return (
          <span className="inline-flex items-center rounded-md bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700">Failed</span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-700">{status}</span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans text-xs -mx-6 -mt-6">
      {/* Header Navigation */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2.5">
          <nav className="flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab === 'Overview') navigate('/timestamps');
                  else if (tab === 'Verification') navigate('/timestamps/verification');
                  else if (tab === 'Time Sources') navigate('/timestamps/TimeSource');
                  else if (tab === 'Authorities') navigate('/timestamps/authorities');
                  else if (tab === 'Settings') navigate('/timestamps/settings');
                }}
                className={`pb-1 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-indigo-600 text-indigo-600 font-semibold'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
          <button className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-indigo-700 transition">
            <Plus className="h-3.5 w-3.5" />
            Create Timestamp
          </button>
        </div>
      </header>

      {/* Main Body */}
      <div className="mx-auto max-w-[1600px] space-y-4 px-6 pt-4">
        {/* Top 5 Metric Cards */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {metrics.map((m) => (
            <div
              key={m.title}
              className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-white px-2 py-1.5 shadow-2xs"
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${m.iconBg}`}>
                {m.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 truncate pl-1" style={{lineHeight:1}}>{m.title}</p>
                <p className="text-lg font-extrabold text-slate-900 pl-1" style={{lineHeight:1.1}}>{m.value}</p>
                <p className={`text-[10px] font-semibold pl-1 ${m.trendColor}`} style={{lineHeight:1}}>{m.trend}</p>
              </div>
            </div>
          ))}
        </section>

        {/* Filter Controls Bar */}
        <section className="flex items-center justify-between gap-2.5 rounded-xl border border-slate-200/90 bg-white p-2.5 shadow-2xs overflow-x-auto">
          <div className="flex flex-1 items-center gap-2.5 min-w-0">
            {/* Search Input */}
            <div className="relative min-w-[220px] flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by file name, hash, ID or reference..."
                className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-xs text-slate-700 outline-none transition focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              />
              <Search className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Type Dropdown */}
            <div className="relative">
              <span className="absolute top-0 -translate-y-1/2 left-2.5 bg-white px-1 text-[10px] font-medium text-slate-500 z-10 leading-none">
                Type
              </span>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-8 min-w-[75px] appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              >
                <option>All</option>
                <option>Document</option>
                <option>Artifact</option>
                <option>Certificate</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Status Dropdown */}
            <div className="relative">
              <span className="absolute top-0 -translate-y-1/2 left-2.5 bg-white px-1 text-[10px] font-medium text-slate-500 z-10 leading-none">
                Status
              </span>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="h-8 min-w-[75px] appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              >
                <option>All</option>
                <option>Verified</option>
                <option>Pending</option>
                <option>Failed</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Authority Dropdown */}
            <div className="relative">
              <span className="absolute top-0 -translate-y-1/2 left-2.5 bg-white px-1 text-[10px] font-medium text-slate-500 z-10 leading-none">
                Authority
              </span>
              <select
                value={filterAuthority}
                onChange={(e) => setFilterAuthority(e.target.value)}
                className="h-8 min-w-[80px] appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              >
                <option>All</option>
                <option>I-PQSign TSA 01</option>
                <option>I-PQSign TSA 02</option>
                <option>GlobalSign TSA</option>
                <option>Sectigo TSA</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* From Date */}
            <div className="relative">
              <span className="absolute top-0 -translate-y-1/2 left-2.5 bg-white px-1 text-[10px] font-medium text-slate-500 z-10 leading-none">
                From Date
              </span>
              <input
                type="text"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-8 w-28 rounded-lg border border-slate-200 bg-white px-2.5 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              />
              <Calendar className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* To Date */}
            <div className="relative">
              <span className="absolute top-0 -translate-y-1/2 left-2.5 bg-white px-1 text-[10px] font-medium text-slate-500 z-10 leading-none">
                To Date
              </span>
              <input
                type="text"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-8 w-28 rounded-lg border border-slate-200 bg-white px-2.5 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
              />
              <Calendar className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* More Filters Button */}
            <button className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 transition hover:bg-slate-50">
              <Filter className="h-3.5 w-3.5 text-slate-500" />
              More Filters
            </button>
          </div>

          {/* Download Button */}
          <button className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50">
            <Download className="h-4 w-4" />
          </button>
        </section>

        {/* Main Content: Table & Right Sidebar */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Timestamp Records Table Card (Cols 9) */}
          <div className="lg:col-span-10 rounded-xl border border-slate-200/90 bg-white shadow-2xs flex flex-col justify-between overflow-hidden">
            <div>
              <div className="border-b border-slate-100 p-3.5">
                <h2 className="text-sm font-bold text-slate-900">Timestamp Records</h2>
              </div>

              {/* Data Table */}
              <div>

                <table className="w-full text-left text-[10px] text-slate-600">
                  <thead className="bg-slate-50/70 text-[9px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                    <tr>
                      <th className="py-1.5 px-2">ID</th>
                      <th className="py-1.5 px-2">Type</th>
                      <th className="py-1.5 px-2">File / Reference</th>
                      <th className="py-1.5 px-2">SHA-256 Hash</th>
                      <th className="py-1.5 px-2 cursor-pointer">Timestamp <span className="text-[8px]">↕</span></th>
                      <th className="py-1.5 px-2">Authority</th>
                      <th className="py-1.5 px-2">Status</th>
                      <th className="py-1.5 px-2">Verification Time</th>
                      <th className="py-1.5 px-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-normal">
                    {records.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50/60 transition">
                        <td className="py-1.5 px-2 font-medium text-indigo-600">{row.id}</td>
                        <td className="py-1.5 px-2">
                          <div className="flex items-center gap-1">
                            {getTypeIcon(row.type)}
                            <span>{row.type}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 font-semibold text-slate-800">{row.file}</td>
                        <td className="py-1.5 px-2 font-mono text-[9px] text-indigo-600">
                          <div className="flex items-center gap-1">
                            <span>{row.hash}</span>
                            <Copy className="h-2.5 w-2.5 text-slate-400 hover:text-indigo-600 cursor-pointer" />
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-slate-700">{row.timestamp}</td>
                        <td className="py-1.5 px-2 text-slate-700">{row.authority}</td>
                        <td className="py-1.5 px-2">{getStatusBadge(row.status)}</td>
                        <td className="py-1.5 px-2 text-slate-700">{row.verificationTime}</td>
                        <td className="py-1.5 px-2">
                          <div className="flex items-center justify-center gap-1.5 text-slate-400">
                            <Eye className="h-3 w-3 hover:text-slate-700 cursor-pointer" />
                            <Download className="h-3 w-3 hover:text-slate-700 cursor-pointer" />
                            <MoreHorizontal className="h-3 w-3 hover:text-slate-700 cursor-pointer" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 p-3 text-slate-500">
              <span>Showing 1 to 10 of 12,458 records</span>

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <button className="flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-slate-400 hover:bg-slate-50">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className={`flex h-7 w-7 items-center justify-center rounded font-semibold ${
                      currentPage === 1
                        ? 'bg-indigo-600 text-white'
                        : 'border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    1
                  </button>
                  <button
                    onClick={() => setCurrentPage(2)}
                    className={`flex h-7 w-7 items-center justify-center rounded border border-slate-200 hover:bg-slate-50 ${
                      currentPage === 2 ? 'bg-indigo-600 text-white' : ''
                    }`}
                  >
                    2
                  </button>
                  <button
                    onClick={() => setCurrentPage(3)}
                    className={`flex h-7 w-7 items-center justify-center rounded border border-slate-200 hover:bg-slate-50 ${
                      currentPage === 3 ? 'bg-indigo-600 text-white' : ''
                    }`}
                  >
                    3
                  </button>
                  <span className="px-1 text-slate-400">...</span>
                  <button className="flex h-7 px-2 items-center justify-center rounded border border-slate-200 hover:bg-slate-50">
                    1,246
                  </button>
                  <button className="flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="relative ml-2">
                  <select className="h-7 appearance-none rounded border border-slate-200 bg-white pl-2 pr-6 text-xs text-slate-700 outline-none">
                    <option>10 / page</option>
                    <option>25 / page</option>
                    <option>50 / page</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
            </div>
          </div>

{/* Right Sidebar Widgets (Cols 2) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Records by Status Donut Chart Card */}
            <div className="rounded-xl border border-slate-200/90 bg-white p-2 shadow-2xs">
              <h3 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-1 mb-1.5">
                Records by Status
              </h3>

              {/* Donut Chart SVG */}
              <div className="relative mx-auto my-2 flex h-36 w-36 items-center justify-center">
                <svg className="h-full w-full" viewBox="-2 -2 40 40">
                  {/* Verified Arc (Green) */}
                  <path
                    stroke="#22c55e"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray="94, 100"
                    strokeDashoffset="0"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  {/* Pending Arc (Amber) */}
                  <path
                    stroke="#f59e0b"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray="2, 100"
                    strokeDashoffset="-94"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  {/* Failed Arc (Rose) */}
                  <path
                    stroke="#f43f5e"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray="2, 100"
                    strokeDashoffset="-96"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  {/* Expired Arc (Blue) */}
                  <path
                    stroke="#3b82f6"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray="2, 100"
                    strokeDashoffset="-98"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>

                {/* Donut Center Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <p className="text-[11px] font-semibold text-slate-500" style={{lineHeight:1}}>Total</p>
                  <p className="text-base font-bold text-slate-900" style={{lineHeight:1}}>12,458</p>
                </div>
              </div>

              {/* Status Breakdown Legend */}
              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">Verified</span>
                  </div>
                  <span className="font-semibold text-slate-800">12,201 (97.9%)</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span className="text-slate-600">Pending</span>
                  </div>
                  <span className="font-semibold text-slate-800">239 (1.9%)</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                    <span className="text-slate-600">Failed</span>
                  </div>
                  <span className="font-semibold text-slate-800">18 (0.1%)</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    <span className="text-slate-600">Expired</span>
                  </div>
                  <span className="font-semibold text-slate-800">3 (0.1%)</span>
                </div>
              </div>
            </div>
            {/* Top Authorities Breakdown Card */}
            <div className="rounded-xl border border-slate-200/90 bg-white p-2.5 shadow-2xs">
              <h3 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-1 mb-2">
                Top Authorities
              </h3>
              <div className="space-y-1.5 text-[10px]">
                {[['I-PQSign TSA 01','6,248','50.1%'],['I-PQSign TSA 02','3,124','25.1%'],['GlobalSign TSA','1,856','14.9%'],['Sectigo TSA','856','6.9%'],['Other','374','3.0%']].map(([name, val, pct]) => (
                  <div key={name} className="flex items-center justify-between">
                    <span className="text-slate-600 font-medium">{name}</span>
                    <span className="font-semibold text-slate-800 text-right">{val} <span className="text-slate-400">({pct})</span></span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-1.5 border-t border-slate-100">
                <a href="#" className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 transition">
                  View All Authorities <ArrowUpRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}